/**
 * Learning Center API: completions, acknowledgements and the certificate a
 * completion earns (plan docs/plans/lms-plan.md Appendix A "Progress,
 * attempts, completions", "Sign-offs and external certificates" and
 * "Certificates and the training log"; 7.2 to 7.4, 10.3, 10.4, 18.15, 18.19;
 * WP-34's and WP-35's routes, WP-40's client).
 *
 *   POST /attempts/:id/complete {attest: true}
 *        -> { completion, certificate: {id, code} | null, assignment: {id, status} | null }
 *           (a competency course whose sign-off has not landed answers completion
 *           null with signoff_required true, 7.4)
 *   POST /courses/:id/acknowledge {password, attest: true} (?force=1)
 *        -> { completion, certificate }; 401 LMS_REAUTH_FAILED (7.3, R20, R25)
 *   GET  /me/completions            -> { items, page } (current and superseded, 7.5)
 *   GET  /me/status                 -> { courses: { [courseId]: { status, ... } } }
 *   GET  /certificates/:id/url?disposition=  -> { url, filename } (10 minutes, 10.4)
 *   POST /completions/:id/certificate/retry  -> { certificate } (10.3, G10)
 *   POST /signoff-requests {course_id, observer_id?} -> the request (7.4)
 *
 * THE PASSWORD (7.3, G14): acknowledgeCourse sends it once in the body and
 * keeps no copy. The request opts out of services/api's global 401 handler
 * (`_retry: true`): that handler refreshes the session and REPLAYS a request
 * that answered 401, which would post the password a second time (and write a
 * second lms.reauth.failed row), and when the refresh fails it clears the
 * stored site and user context. A 401 here is the credential re-check saying
 * no, not an expired session.
 */
import api from '../../services/api';
import type { ComplianceStatus } from '../lib/complianceStatus';
import type { LmsList } from './client';

export type CompletionKind = 'quiz' | 'read_ack' | 'competency' | 'external';

/** lms_completions as the routes answer it (5.5). */
export interface CompletionRow {
  id: string;
  user_id?: string;
  course_id: string;
  course_version_id: string;
  kind: CompletionKind;
  completed_at: string;
  /** null: the course does not expire (validity_months NULL). */
  expires_at: string | null;
  score_pct: number | null;
  certificate_id?: string | null;
  assignment_id?: string | null;
  superseded_at?: string | null;
}

export interface CertificateRef {
  id: string;
  code: string;
}

export interface CompletionResult {
  completion: CompletionRow | null;
  certificate: CertificateRef | null;
  assignment?: { id: string; status: string } | null;
  /** 7.4: the quiz half of a competency course is done; the observed sign-off is still needed. */
  signoff_required?: boolean;
}

/** One GET /me/completions transcript row (7.5; WP-34 transcriptItem). */
export interface TranscriptItem {
  id: string;
  course_id: string;
  course: { id: string; title: string; slug: string; category: string; kind: string } | null;
  course_version_id: string;
  version_no: number | null;
  kind: CompletionKind;
  completed_at: string;
  expires_at: string | null;
  score_pct: number | null;
  state: string;
  superseded_at: string | null;
  superseded_reason: string | null;
  certificate_id: string | null;
  certificate: CertificateRef | null;
}

/** GET /me/status cell (Appendix A; 8.4). */
export interface MyCourseStatus {
  status: ComplianceStatus;
  due_at?: string;
  expires_at?: string;
  assignment_id?: string;
  completion_id?: string;
}

export interface MyStatus {
  courses: Record<string, MyCourseStatus>;
}

export interface SignoffRequest {
  id: string;
  course_id: string;
  observer_id: string | null;
  status: 'open' | 'done' | 'cancelled';
  requested_at?: string;
}

const enc = encodeURIComponent;

/** POST /api/lms/attempts/:id/complete (the learner clicked Attest, 7.3). */
export async function completeAttempt(attemptId: string): Promise<CompletionResult> {
  const response = await api.post<CompletionResult>(`/api/lms/attempts/${enc(attemptId)}/complete`, { attest: true });
  return response.data;
}

/**
 * POST /api/lms/courses/:id/acknowledge with the credential re-check (7.3).
 * The password goes in the body once; see the file header for `_retry`.
 */
export async function acknowledgeCourse(
  courseId: string,
  password: string,
  options: { force?: boolean } = {}
): Promise<CompletionResult> {
  const config: Record<string, unknown> = { _retry: true };
  if (options.force) config.params = { force: 1 };
  const response = await api.post<CompletionResult>(
    `/api/lms/courses/${enc(courseId)}/acknowledge`,
    { password, attest: true },
    config
  );
  return response.data;
}

/** GET /api/lms/me/completions */
export async function listMyCompletions(options: { limit?: number; offset?: number } = {}): Promise<LmsList<TranscriptItem>> {
  const params: Record<string, number> = {};
  if (options.limit !== undefined) params.limit = options.limit;
  if (options.offset !== undefined) params.offset = options.offset;
  const response = await api.get<LmsList<TranscriptItem>>('/api/lms/me/completions', { params });
  return response.data;
}

/** GET /api/lms/me/status */
export async function getMyStatus(): Promise<MyStatus> {
  const response = await api.get<MyStatus>('/api/lms/me/status');
  return response.data;
}

/** GET /api/lms/certificates/:id/url (attachment by default, 10.4). */
export async function getCertificateUrl(
  certificateId: string,
  disposition: 'attachment' | 'inline' = 'attachment'
): Promise<{ url: string; filename: string }> {
  const response = await api.get<{ url: string; filename: string }>(`/api/lms/certificates/${enc(certificateId)}/url`, {
    params: { disposition },
  });
  return response.data;
}

/** POST /api/lms/completions/:id/certificate/retry (10.3: "Certificate pending" with Retry). */
export async function retryCertificate(completionId: string): Promise<{ certificate: CertificateRef }> {
  const response = await api.post<{ certificate: CertificateRef }>(`/api/lms/completions/${enc(completionId)}/certificate/retry`, {});
  return response.data;
}

/** POST /api/lms/signoff-requests (7.4: from the Done stage; observer omitted means any admin). */
export async function requestSignoff(courseId: string, observerId?: string | null): Promise<SignoffRequest> {
  const body: { course_id: string; observer_id?: string } = { course_id: courseId };
  if (observerId) body.observer_id = observerId;
  const response = await api.post<SignoffRequest>('/api/lms/signoff-requests', body);
  return response.data;
}
